import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, MinusIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Card, Flex, Spinner, Tabs, Text } from '@radix-ui/themes';
import { useCallback, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import DateFieldComponent from '../../components/inputs/Datefield/DatefieldComponent';
import { DragAndDropFileUpload } from '../../components/inputs/FileUpload/DragAndDropFileUpload';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { useGetTeacherRequiredDocumentsQuery } from '../../hooks/useGetTeacherRequiredDocumentsQuery';
import { useGetTeachersQuery } from '../../hooks/useGetTeachersQuery';
import { resetUpdateTeachertData } from '../../redux/slices/school/teacher';
import { routes } from '../../routes';
import { createTeacher, createTeacherCode, createTeacherDepartament, createTeacherDocument } from '../../services/school/teacher/createTeacher';
import { addUserRole } from '../../services/user/addUserRole';
import { createUser, getUserNameSuggestion } from '../../services/user/createUser';
import { staticRoles } from '../../staticRoles';
import { CreateTeacherType, GenerateCodeTeacherType, TeacherType } from '../../types/Teacher';
import { formatDateForInput, toDateOnly } from '../../utils/dateFormatter';
import { getFile } from '../../utils/fileUtils';
import { CreateTeacherFormValidation } from './CreateTeacherFormValidation';
import { DepartamentType } from '../../types/Departament';
import { getDepartaments } from '../../services/school/departament/getDepartaments';
import { getTeachers } from '../../services/school/teacher/getTeachers';
import { stringToBoolean } from '../../utils/utilities';
import { AcademicLevels, Countries, Country, provinces, teacherCategories } from '../../utils/codes';
import { UserType } from '../../types/User';
import { useGetAcademicLevelsQuery } from '../../hooks/useGetAcademicLevelsQuery';
import { useGetFormationAreasQuery } from '../../hooks/useGetFormationAreasQuery';
import { debounce } from 'lodash';
import { getFaculties } from '../../services/school/faculty/getFaculties';
import { FacultyType } from '../../types/Faculty';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import { useGetKnowledgeDomainsQuery } from '../../hooks/useGetKnowledgeDomainsQuery';
type ActiveSegmentTab = 'user' | 'documents' | 'departament';

export const CreateTeacher = () => {
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('user');

    const mutationCreateUser = useMutation({ mutationFn: createUser, mutationKey: "createUser" })
    const mutationAddUserRole = useMutation({ mutationKey: "addUserRole", mutationFn: addUserRole });
    const mutationCreateTeacherCode = useMutation({ mutationFn: createTeacherCode, mutationKey: "createTeacherCode" })
    const mutationCreateTeacher = useMutation({ mutationFn: createTeacher, mutationKey: "createTeacher" })
    const mutationCreateTeacherDepartament = useMutation({ mutationFn: createTeacherDepartament, mutationKey: "createTeacherDepartament" })
    const mutationCreateTeacherDocument = useMutation({ mutationFn: createTeacherDocument, mutationKey: "createTeacherDocument" })
    const { data: currentYear } = useGetCurrentAcademicYearQuery();
    const loadingState = mutationCreateUser.isLoading || mutationAddUserRole.isLoading || mutationCreateTeacherCode.isLoading || mutationCreateTeacher.isLoading || mutationCreateTeacherDepartament.isLoading || mutationCreateTeacherDocument.isLoading;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { data: knowledgeDomains, isError: isErrorKnowledgeDomains, isLoading: isLoadingKnowledgeDomains } = useGetKnowledgeDomainsQuery({ PageNumber: 1, PageSize: 100 });

    const defaultValues: CreateTeacherType = {
        user: {
            id: undefined,
            name: '',
            userName: '',
            email: '',
            bi: '',
            nuit: '',
            phoneNumber: '',
            dateOfBirth: '',
            gender: 'M',
            role: undefined,
            password: undefined,
            isForeign: false
        },
        teacher: {
            id: undefined,
            status: undefined,
            enrollmentDate: undefined,
            teacherUniversalCode: undefined,
            endDate: undefined,
            idUser: undefined,
            idTeacherSupervisor: undefined,
            idDepartament: '',
            startDate: formatDateForInput((new Date().toDateString())),
            isLinked: true
        },
        documents: [],
        idFaculty: '',
        idDepartament: '',
        teacherDepartaments: [{
            idDepartament: "",
            idTeacher: '',
            startDate: '',
            status: true,
            idFaculty: '',
            idUniversity: '',
        }]
    }


    const { control, handleSubmit, formState: { errors }, getValues, setValue, trigger, resetField, watch } = useForm<CreateTeacherType>({
        resolver: yupResolver(CreateTeacherFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const { fields, append, remove, } = useFieldArray({
        control,
        name: 'teacherDepartaments',
    });
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
        trigger();
    }
    const handleBack = () => {
        dispatch(resetUpdateTeachertData());
        navigate(routes.teachers);
    }

    const handleEmailChange = (e: any) => {
        var email = e.target.value;
        setValue("user.email", email);
        if (email) {
            setValue("user.userName", email);
        }
    }
    const onSubmit = handleSubmit(async (data) => {
         const birthYear = data.user.dateOfBirth ? new Date(data.user.dateOfBirth).getFullYear() : null;
  const formationYear = data.teacher.formationYear ? parseInt(data.teacher.formationYear) : null;

  if (birthYear && formationYear && formationYear < birthYear) {
    toast.error("O ano de formação não pode ser inferior ao ano de nascimento");
    return; 
  }
  
        const resultCreateUser = await mutationCreateUser.mutateAsync(data.user as UserType)
        if (!resultCreateUser) return;
        const { id: idUser } = resultCreateUser as any
        const resultAddUserRole = await mutationAddUserRole.mutateAsync({
            roleId: staticRoles.Teacher,
            userId: idUser
        })
        if (!resultAddUserRole) return;
        const { nuit, bi, } = data.user;
        const resultCreateTeacherCode = await mutationCreateTeacherCode.mutateAsync({
            nuit: data.user.nuit ?? "",
            bi,
            idDepartament: data.idDepartament,
            year: currentYear?.year!,
        })
        if (!resultCreateTeacherCode) return;
        const resultCreateTeacher = await mutationCreateTeacher.mutateAsync({
            ...data.teacher,
            enrollmentDate: toDateOnly(new Date()),
            teacherUniversalCode: resultCreateTeacherCode,
            idTeacherSupervisor: data.teacher.idTeacherSupervisor,
            idUser,
            fullname: data.user.name,
            startDate: data.teacher.startDate,
            idDepartament: '',
            isForeign: data?.user?.isForeign,
        });
        if (!resultCreateTeacher) return;
        await Promise.all(
            (data.teacherDepartaments ?? [])
                .map(td => {
                    td.idTeacher = resultCreateTeacher!;
                    return mutationCreateTeacherDepartament.mutateAsync(td);
                })
        );
        const convertedToBase64Docs = await Promise.all(data.documents?.map(async (document) => {
            return getFile(document.file, document.id)
        }))
       await Promise.all(convertedToBase64Docs.map((cv) => {
            return mutationCreateTeacherDocument.mutateAsync({
                docBase64: cv.base64,
                name: cv.name,
                extension: cv.extension,
                idDocument: cv.id,
                idTeacher: resultCreateTeacher
            })
        }))
       
        toast.success("Docente criado");
        handleBack();
    })
  const { data:universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery({ PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
        { enabled: !!getValues('idFaculty') }
    );
    const { data: documentsData } = useGetTeacherRequiredDocumentsQuery({ PageNumber: 1, PageSize: 400 });


    const { data: teachersData, isLoading: isLoadingTeacherData, isError: isErrorTeacherData, refetch: refreshTeachersData } = useGetTeachersQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('teacher.idDepartament') },
        { enabled: !!getValues('teacher.idDepartament') }
    );

    const handleFacultyChange = (idFaculty: string) => {
        setValue('idFaculty', idFaculty);
        refreshDepartaments();
    };
    const handleDepartamentChange = (idDepartament: string) => {
        setValue('teacher.idDepartament', idDepartament);
        refreshTeachersData();
    };

    const handleAddDepartament = () => {
        append(
            {
                id: '',
                idTeacher: '',
                idDepartament: '',
                startDate: '',
                status: true,
                idFaculty: ''
            });
    };
    const [filteredDepartaments, setFilteredDepartaments] = useState<{ [key: number]: DepartamentType[] }>({});
    const [FilteredFaculties, setFilteredFaculties] = useState<{ [key: number]: FacultyType[] }>({});
    const [filteredTeachersDepartaments, setTeachersDepartaments] = useState<{ [key: number]: TeacherType[] }>({});

    const handleUniversityChangeV2 = async (idUniversity: string, index: number) => {
        setValue(`teacherDepartaments.${index}.idUniversity`, idUniversity);
        const faculties = await getFaculties({ PageSize: 400, PageNumber: 1, IdUniversity: idUniversity });
        if (faculties?.items) {
            const filtereds = faculties.items.filter(
                faculty => faculty.idUniversity === idUniversity
            );
            setFilteredFaculties(prev => ({
                ...prev,
                [index]: filtereds
            }));
        }
    };
    const handleFacultyChangeV2 = async (idFaculty: string, index: number) => {
        setValue(`teacherDepartaments.${index}.idFaculty`, idFaculty);
        const departaments = await getDepartaments({ PageSize: 400, PageNumber: 1, IdFaculty: idFaculty });
        if (departaments?.items) {
            const filteredDepartaments = departaments.items.filter(
                faculty => faculty.idFaculty === idFaculty
            );
            setFilteredDepartaments(prev => ({
                ...prev,
                [index]: filteredDepartaments
            }));
        }
    };

    const handleDepartamentChangeV2 = async (idDepartament: string, index: number) => {
        setValue(`teacherDepartaments.${index}.idDepartament`, idDepartament);
        const teachers = await getTeachers({ PageSize: 400, PageNumber: 1, IdDepartament: idDepartament });
        if (teachers?.items) {
            const filteredTeachers = teachers.items.filter(
                teacher => teacher.idDepartament === idDepartament
            );
            setTeachersDepartaments(prev => ({
                ...prev,
                [index]: filteredTeachers
         }));
        }
    };
    const watchIsForeign = watch('user.isForeign');
    const handleOnChangeIsForeign = (e: any) => {
        const isIsForeign = stringToBoolean(getValues('user.isForeign'))
        if (isIsForeign) {
            resetField('user.nuit')
            setValue('teacher.country', '')
        }
        if (!isIsForeign) {
            setValue('teacher.country', 'MOZ')
        }
    };
    const fetchUserNameSuggestion = async (fullName: string) => {
        if (!fullName) return;
        //  setLoadingUserName(true);
        try {
            const response = await getUserNameSuggestion({ name: fullName });
            setValue('user.userName', response);
            //   setUserNameError(null);
        } catch (error: any) {
            // setUserNameError(error.message || 'Erro ao buscar sugestão');
        } finally {
            //setLoadingUserName(false);
        }
    };


    const debouncedFetchUserName = useCallback(debounce(fetchUserNameSuggestion, 500), []);
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setValue('user.name', name);
        debouncedFetchUserName(name);
    };
    return (
        <form onSubmit={onSubmit} noValidate>
            <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
                navigateAndValidate(tabToNavigate as ActiveSegmentTab);
            }}>
                <Tabs.List>
                    <Tabs.Trigger value='user' >
                        Dados gerais {errors?.user && <Badge color="red">{Object.keys(errors?.user)?.length} </Badge>}
                    </Tabs.Trigger>
                    <Tabs.Trigger value='departament'>
                        Departamentos  {errors?.teacherDepartaments && <Badge color="red">{Object.keys(errors?.teacherDepartaments)?.length} </Badge>}
                    </Tabs.Trigger>
                    <Tabs.Trigger value='documents'>
                        Documentos {errors?.documents && <Badge color="red">{Object.keys(errors?.documents)?.length} </Badge>}
                    </Tabs.Trigger>
                </Tabs.List>
                <Box pt="3">
                    <Tabs.Content value="user">
                        <Flex direction='column' gap='4'>
                            <Card size='3'>
                                <Flex direction='column' gap='4'>
                                    <Flex gap="8">
                                        <TextFieldComponent name="user.name" composedName={{ parent: "user", prop: 'name' }} label="Nome" control={control} errors={errors} fieldProps={{ placeholder: 'Nome completo', onChange: handleNameChange }} style={{ width: '100%' }} required={true} />
                                        <TextFieldComponent name="user.email" composedName={{ parent: "user", prop: 'email' }} label="Email" control={control} errors={errors} fieldProps={{ placeholder: 'seu@email.com', onChange: handleEmailChange, type: 'email' }} style={{ width: '100%' }} required={true} />
                                        <TextFieldComponent name="user.secondaryEmail" label='Email alternativo' control={control} errors={errors} fieldProps={{ placeholder: 'seu@email.com', type: 'email' }} style={{ width: '100%' }} />

                                    </Flex>
                                    <Flex gap='6'>
                                        <RadioGroupComponent name="user.isForeign" label="Estrangeiro" control={control} errors={errors}
                                            style={{ width: '50%' }}
                                            options={[
                                                { value: "true", label: "Sim" }
                                                , { value: "false", label: "Não" }]}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                                onChange: handleOnChangeIsForeign
                                            }}
                                        />
                                        <SelectComponent
                                            name="teacher.country"
                                            label="País"
                                            control={control}
                                            errors={errors}
                                            options={Countries?.map((country: Country) => ({
                                                value: country.code,
                                                label: country.label ?? "",
                                            }))}
                                            style={{ width: '100%' }}
                                            fieldProps={{ disabled: !stringToBoolean(watchIsForeign) }}
                                        />
                                    </Flex>
                                    <Flex gap="8">
                                        <TextFieldComponent name="user.bi" composedName={{ parent: "user", prop: 'bi' }} label="BI/DIRE/Passaporte/Carta de condução/Etc" control={control} errors={errors} fieldProps={{ placeholder: 'BI/DIRE/Passaporte/Carta de condução/Etc' }} style={{ width: '100%' }} required={true} />
                                        <TextFieldComponent name="user.nuit" composedName={{ parent: "user", prop: 'nuit' }} label="NUIT" control={control} errors={errors} fieldProps={{ placeholder: 'Seu NUIT' }} style={{ width: '100%' }} required={true} />
                                        <TextFieldComponent name="user.phoneNumber" composedName={{ parent: "user", prop: 'phoneNumber' }} label="Telefone" control={control} errors={errors} fieldProps={{ placeholder: '123456789' }} style={{ width: '100%' }} required={true} />
                                        <TextFieldComponent name="user.secondaryPhoneNumber" label='Telefone alternativo' control={control} errors={errors} fieldProps={{ placeholder: '123456789' }} style={{ width: '100%' }} />

                                    </Flex>
                                    <Flex gap="8">

                                        <DateFieldComponent name="user.dateOfBirth" composedName={{ parent: "user", prop: 'dateOfBirth' }} label="Data de Nascimento" control={control} errors={errors} style={{ width: '45%' }} required={true} />
                                        <RadioGroupComponent name="user.gender" composedName={{ parent: "user", prop: 'gender' }} label="Gênero" control={control} errors={errors} style={{ width: '68%' }}
                                            options={[
                                                { value: "M", label: "Masculino" }
                                                , { value: "F", label: "Feminino" }]}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                                            }}
                                        />
                                         <SelectComponent
                                            name="teacher.province"
                                            label="Provincia"
                                            control={control}
                                            errors={errors}
                                            composedName={{ parent: "teacher", prop: 'province' }}
                                            options={provinces?.map((country: { id: any, description: any }) => ({
                                                value: country.id,
                                                label: country.description ?? "",
                                            }))}
                                            style={{ width: '100%' }}
                                        />
                                    </Flex>
                                    <Flex gap='4'>
                                        <RadioGroupComponent name="teacher.typeContract" label="Regime de Contratação" control={control} errors={errors}
                                            style={{ width: '50%' }}
                                            options={[
                                                { value: "1", label: "Parcial" }
                                                , { value: "2", label: "Tempo inteiro" }]}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                            }}
                                            required={true}
                                        />
                                        <SelectComponent
                                            name="teacher.category"
                                            label="Categoria"
                                            required={true}
                                            control={control}
                                            errors={errors}
                                            composedName={{ parent: "teacher", prop: 'category' }}
                                            options={teacherCategories?.map((t: { id: any, description: any }) => ({
                                                value: t.id,
                                                label: t.description ?? "",
                                            }))}
                                            style={{ width: '100%' }}
                                        />

                                    </Flex>
                                    <Flex gap='4'>
                                       
                                        <TextFieldComponent name="teacher.formationLocal"  composedName={{ parent: "teacher", prop: 'formationLocal' }} label="Local de formação" control={control} errors={errors} fieldProps={{ placeholder: '' }} style={{ width: '100%' }} />
                                        <TextFieldComponent name="teacher.formationYear" composedName={{ parent: "teacher", prop: 'formationYear' }} label="Ano de formação" control={control} errors={errors} fieldProps={{ placeholder: '' }} style={{ width: '100%' }} />
                                    </Flex>
                                    <Flex gap='4'>
                                        <SelectComponent
                                            required={true}
                                            name="teacher.academicLevel"
                                            label="Nível de formação"
                                            control={control}
                                            errors={errors}
                                            composedName={{ parent: "teacher", prop: 'academicLevel' }}
                                            options={AcademicLevels?.map((t: { id: any, description: any }) => ({
                                                value: t.id,
                                                label: t.description ?? "",
                                            }))}                                            style={{ width: '100%' }}
                                        />
                                        <SelectComponent
                                            name="teacher.idKnowledgeDomain"
                                            label="área de formação"
                                            required={true}
                                            control={control}
                                            errors={errors}
                                            composedName={{ parent: "teacher", prop: 'idKnowledgeSubDomain' }}
                                            options={knowledgeDomains?.items.map(i => ({ value: i.id ?? "", label: i.description ?? "" }))}
                                            style={{ width: '100%' }}
                                            itemsLoading={isLoadingKnowledgeDomains}
                                            itemsError={isErrorKnowledgeDomains}
                                        />

                                    </Flex>
                                 

                                    <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                                        <Button color='red'
                                            onClick={handleBack}
                                            disabled={loadingState}
                                        >
                                            <ChevronLeftIcon /> Sair
                                        </Button>

                                        <Button onClick={() => {
                                            navigateAndValidate("departament")
                                        }}
                                            variant='surface' >
                                            {false ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                            Proximo
                                        </Button>
                                    </Flex>

                                </Flex>

                            </Card>
                        </Flex>
                    </Tabs.Content>
                    <Tabs.Content value="departament">
                        <Flex direction='column' gap='4'>
                            <Box>
                                <Button onClick={(e) => {
                                    e.preventDefault();
                                    handleAddDepartament();
                                }} variant="outline">Adicionar Departamento</Button>
                            </Box>
                            {fields.map((field, index) => (
                                <Card size='3' key={field.id}>
                                    <Flex justify='end'>
                                        <Button onClick={async (e) => {
                                            e.preventDefault();
                                            remove(index)
                                        }
                                        } color="red"><MinusIcon width="18" height="18" />
                                        </Button>
                                    </Flex>
                                    <Flex direction='column' gap='4'>
                                        <Flex gap="8">
                                            <Box style={{ width: "100%" }}>
                                                <SelectComponent
                                                    name={`teacherDepartaments.${index}.idUniversity`}
                                                    label="Instituição de Ensino Superior"
                                                    control={control}
                                                    errors={errors}
                                                    options={universities?.items.map(u => ({ value: u.id ?? "", label: u.description ?? "" }))}
                                                    onSelect={async (id: string) => {
                                                        await handleUniversityChangeV2(id, index);
                                                    }}
                                                    style={{ width: '100%' }}
                                                    required={true}
                                                />
                                                {errors.teacherDepartaments?.[index]?.idUniversity && (
                                                    <Text color="red" size="1">{`${errors.teacherDepartaments[index]?.idUniversity?.message}`}</Text>
                                                )}

                                            </Box>
                                            <Box style={{ width: "100%" }}>
                                                <SelectComponent
                                                    name={`teacherDepartaments.${index}.idFaculty`}
                                                    label="Unidade orgânica"
                                                    control={control}
                                                    errors={errors}
                                                    options={FilteredFaculties?.[index]?.map(f => ({ value: f.id ?? "", label: f.description ?? "" }))}
                                                    onSelect={async (id: string) => {
                                                        await handleFacultyChangeV2(id, index);
                                                    }}
                                                    style={{ width: '100%' }}
                                                    required={true}
                                                />
                                                {errors.teacherDepartaments?.[index]?.idFaculty && (
                                                    <Text color="red" size="1">{`${errors.teacherDepartaments[index]?.idFaculty?.message}`}</Text>
                                                )}

                                            </Box>
                                            <Box style={{ width: "100%" }}>
                                                <SelectComponent
                                                    name={`teacherDepartaments.${index}.idDepartament`}
                                                    label="Departamento"
                                                    control={control}
                                                    errors={errors}
                                                    options={filteredDepartaments?.[index]?.map(departament => ({ value: departament.id ?? "", label: departament.description ?? "" }))}
                                                    onSelect={async (id: string) => {
                                                        await handleDepartamentChangeV2(id, index);
                                                    }}
                                                    style={{ width: '100%' }}
                                                    required={true}
                                                />
                                                {errors.teacherDepartaments?.[index]?.idDepartament && (
                                                    <Text color="red" size="1">{`${errors.teacherDepartaments[index]?.idDepartament?.message}`}</Text>
                                                )}

                                            </Box>
                                        </Flex>
                                        <Flex gap="8">
                                            <Box style={{ width: "100%" }}>
                                                <DateFieldComponent
                                                    name={`teacherDepartaments.${index}.startDate`}
                                                    label="Data de Ingresso"
                                                    control={control}
                                                    errors={errors}
                                                    style={{ width: '100%' }}
                                                    required={true}
                                                />
                                                {errors.teacherDepartaments?.[index]?.startDate && (
                                                    <Text color="red" size="1">{`${errors.teacherDepartaments[index]?.startDate?.message}`}</Text>
                                                )}

                                            </Box>
                                            <Box style={{ width: "100%" }}>
                                                <SelectComponent
                                                    name={`teacherDepartaments.${index}.idTeacherSupervisor`}
                                                    label="Assistente de"
                                                    control={control}
                                                    errors={errors}
                                                    style={{ width: '100%' }}
                                                    options={filteredTeachersDepartaments?.[index]?.map(teacher => ({
                                                        value: teacher.id ?? "",
                                                        label: `${teacher?.fullname} - ${teacher.teacherUniversalCode}`
                                                    }))}
                                                />
                                                {errors.teacherDepartaments?.[index]?.idTeacherSupervisor && (
                                                    <Text color="red" size="1">{`${errors.teacherDepartaments[index]?.idTeacherSupervisor?.message}`}</Text>
                                                )}

                                            </Box>

                                        </Flex>
                                    </Flex>
                                </Card>
                            ))}
                        </Flex>
                    </Tabs.Content>
                    <Tabs.Content value="documents">
                        <Card size='3'>
                            <Flex direction='column' gap='4'>
                                {documentsData?.items?.map((requiredDoc, index) => (
                                    <Box key={requiredDoc.id} style={{ width: '100%' }}>
                                        <Text as="label" size="2"><span>{requiredDoc.description} <span style={{ color: 'red' }}>*</span></span></Text>
                                        <Controller
                                            name={`documents.${index}.file` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <DragAndDropFileUpload
                                                    onFileDrop={(file) => {
                                                        field.onChange(file);
                                                        setValue(`documents.${index}.id`, requiredDoc?.id!);
                                                    }}
                                                    label={`Arraste ou selecione o arquivo para ${requiredDoc.description}`}
                                                    accept="*"
                                                    selectedFile={field.value}
                                                />
                                            )}
                                        />
                                        {errors.documents?.[index]?.file && (
                                            <Text color="red" size="1">{`${errors.documents[index]?.file?.message} ${requiredDoc.description}`}</Text>
                                        )}
                                    </Box>
                                ))}
                                <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                                    <Button color='red' onClick={() => {
                                        navigateAndValidate("departament")
                                    }}
                                    >
                                        <ChevronLeftIcon /> Voltar
                                    </Button>
                                    <Button type='submit' variant='surface'
                                        disabled={loadingState}
                                    >
                                        {loadingState ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                        Submeter
                                    </Button>
                                </Flex>
                            </Flex>
                        </Card>

                    </Tabs.Content>
                </Box>
            </Tabs.Root>
        </form>
    )
}



